import { Item, ItemActions, ItemDescription, ItemHeader, ItemTitle } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { cn } from '@/shared/lib';
import type { SpeakerSide } from '../speaker';
import { PHRASE_ROW_CLASS, PHRASE_TIME_CLASS } from './recordSide';

/** Подписанное значение строки: так перебивание называет свои реплики */
export type TranscriptRecordDetail = {
  label: string;
  value: string;
};

type Props = {
  recordId: number;
  /** Говорящий реплики или вид события */
  title: string;
  timeRange: string;
  /** Сторона ленты реплики — её цветами окрашена строка; у событий и реплики без говорящего её нет */
  side?: SpeakerSide;
  /** Текст реплики */
  text?: string;
  /** Класс размера шрифта текста реплики; заголовок и границы строки остаются своего размера */
  textClassName: string;
  details?: TranscriptRecordDetail[];
  actions?: ReactNode;
  /** Реплика выбирается в поле перебивания нажатием на строку */
  onPick?: () => void;
};

/**
 * Строка ленты режима редактирования: одна запись расшифровки, окрашенная как в расшифровке вне режима.
 */
export const TranscriptRecordView: FC<Props> = ({
  recordId,
  title,
  timeRange,
  side,
  text,
  textClassName,
  details,
  actions,
  onPick,
}) => (
  <Item
    variant={side ? 'default' : 'outline'}
    size="sm"
    data-message-id={recordId}
    role={onPick ? 'button' : undefined}
    tabIndex={onPick ? -1 : undefined}
    onClick={onPick}
    className={cn(
      'min-w-0',
      side ? PHRASE_ROW_CLASS[side] : 'text-(--color-chat-event)',
      onPick && 'cursor-pointer ring-1 ring-primary hover:opacity-80',
    )}
  >
    {/* Заголовок занимает в строке ширину своего самого длинного слова: если рядом с ним не умещаются время
        и действия, они вместе уходят строкой под заголовок (и сами переносятся, если не умещаются и там).
        Внутри слова имя рвётся, только когда слово шире всей строки, — и не наезжает на время */}
    <ItemHeader className="min-w-0 flex-wrap gap-2">
      <ItemTitle className="w-min! min-w-0 grow">
        <span className="min-w-0 wrap-break-word">{title}</span>
      </ItemTitle>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <span className={cn('whitespace-nowrap text-xs', side ? PHRASE_TIME_CLASS[side] : 'text-(--color-chat-event)')}>
          {timeRange}
        </span>
        {/* Действия — на фоне карточки, а не облака: значок удаления своего цвета на заливке облака не читается */}
        {actions && <ItemActions className="rounded-md bg-card">{actions}</ItemActions>}
      </div>
    </ItemHeader>
    {/* Текст реплики — не описание строки: читается целиком, без усечения, которое kit даёт описанию */}
    {text && <p className={cn('w-full whitespace-pre-wrap wrap-break-word', textClassName)}>{text}</p>}
    {details?.map((detail) => (
      // Цвет описания kit задаёт сам — цвет отметки события ставится поверх него с важностью
      <ItemDescription key={detail.label} className="w-full wrap-break-word text-(--color-chat-event)!">
        {`${detail.label}: ${detail.value}`}
      </ItemDescription>
    ))}
  </Item>
);
